'use client';

import {
	BN,
	BigNum,
	L2OrderBook,
	OraclePriceData,
	PRICE_PRECISION_EXP,
	uncrossL2,
} from '@velocity-exchange/sdk';
import { useCallback, useRef, useEffect } from 'react';
import useCurrentUserBidsAndAsks from '../useCurrentUserBidsAndAsks';
import useMarketInfoDisplayData from '../useMarketInfoDisplayData';
import useGetTickSizeForMarket from '../useGetTickSizeForMarket';
import { L2WithOracle, MarketId } from '@velocity-exchange/common';

export const useUncrossOrderbook = () => {
	const { getUserBidsAndAsksForMarket } = useCurrentUserBidsAndAsks();
	const marketData = useMarketInfoDisplayData();

	const getTickSizeForMarket = useGetTickSizeForMarket();

	const marketDataRef = useRef(marketData);
	const getUserBidsAndAsksRef = useRef(getUserBidsAndAsksForMarket);

	useEffect(() => {
		marketDataRef.current = marketData;
		getUserBidsAndAsksRef.current = getUserBidsAndAsksForMarket;
	}, [marketData, getUserBidsAndAsksForMarket]);

	const uncrossL2OrderbookWithOracle = useCallback(
		(l2WithOracle: L2WithOracle, marketId: MarketId) => {
			const userBidsAndAsksForMarket = getUserBidsAndAsksRef.current(
				marketId.key
			);

			return uncrossL2(
				l2WithOracle.bids,
				l2WithOracle.asks,
				l2WithOracle.oracleData.price,
				new BN(+marketDataRef.current.oracleTwapLive),
				new BN(+marketDataRef.current.markTwapLive),
				getTickSizeForMarket(marketId),
				new Set(
					userBidsAndAsksForMarket.bids.map((b) =>
						BigNum.fromPrint(b.price.toString(), PRICE_PRECISION_EXP).toString()
					)
				),
				new Set(
					userBidsAndAsksForMarket.asks.map((a) =>
						BigNum.fromPrint(a.price.toString(), PRICE_PRECISION_EXP).toString()
					)
				)
			);
		},
		[getTickSizeForMarket]
	);

	const uncrossL2Orderbook = useCallback(
		(
			l2Orderbook: L2OrderBook,
			oraclePriceData: OraclePriceData,
			marketId: MarketId
		) => {
			const userBidsAndAsksForMarket = getUserBidsAndAsksRef.current(
				marketId.key
			);

			return uncrossL2(
				l2Orderbook.bids,
				l2Orderbook.asks,
				oraclePriceData.price,
				new BN(+marketDataRef.current.oracleTwapLive),
				new BN(+marketDataRef.current.markTwapLive),
				getTickSizeForMarket(marketId),
				new Set(
					userBidsAndAsksForMarket.bids.map((b) =>
						BigNum.fromPrint(b.price.toString(), PRICE_PRECISION_EXP).toString()
					)
				),
				new Set(
					userBidsAndAsksForMarket.asks.map((a) =>
						BigNum.fromPrint(a.price.toString(), PRICE_PRECISION_EXP).toString()
					)
				)
			);
		},
		[getTickSizeForMarket]
	);

	return {
		uncrossL2OrderbookWithOracle,
		uncrossL2Orderbook,
	};
};
